import { useEffect, useState, type CSSProperties, type ReactNode, type Ref } from 'react';
import { DAALUU_KINDS, type DaaluuPlay, type DaaluuTile as Tile, type GameView } from '@big2/shared';
import { DaaluuTile } from './DaaluuTile.js';
import { Icon } from '../../Icon.js';
import { useGameState } from '../../../net/GameProvider.js';
import { useTableInteractions } from '../TableInteractions.js';
import { DaaluuHouseDrag, DaaluuHouseGuides } from './DaaluuHouseTrade.js';

/** Tile order is bottom-to-top. Faces are public only after being played. */
function TilePile({ tiles, label, deadTileIds = [], movingTileIds = [], mn }: { tiles: Tile[]; label: string; deadTileIds?: string[]; movingTileIds?: string[]; mn: boolean }) {
  return <span className="daaluu-captured-pile" data-daaluu-capture-house={tiles[0]?.id} role="group" aria-label={label}>
    {tiles.map((tile, index) => <span className={`daaluu-pile-layer${movingTileIds.includes(tile.id) ? ' daaluu-tile-arriving' : ''}`} key={tile.id}
      style={{ '--layer': index } as CSSProperties}><DaaluuTile tile={tile} dead={deadTileIds.includes(tile.id)} deadLabel={mn ? 'Үхсэн' : 'Dead'} /></span>)}
  </span>;
}

export function DaaluuPlayedPile({ plays, winningSeat, players, mn, arrivingTileIds = [] }: {
  plays: DaaluuPlay[]; winningSeat: number | null; players: GameView['players']; mn: boolean; arrivingTileIds?: string[];
}) {
  const [settledWinnerSeat, setSettledWinnerSeat] = useState(winningSeat);
  const winnerArriving = typeof window !== 'undefined' && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches &&
    plays.some(play => play.seat === winningSeat && play.tiles.some(tile => arrivingTileIds.includes(tile.id)));
  useEffect(() => { if (!winnerArriving) setSettledWinnerSeat(winningSeat); }, [winnerArriving, winningSeat]);
  if (!plays.length) return null;
  const topSeat = winnerArriving && plays.some(play => play.seat === settledWinnerSeat) ? settledWinnerSeat : winningSeat;
  const winner = players.find(player => player.seat === winningSeat);
  const renderTiles = (play: DaaluuPlay) => {
    const byKind = new Map<string, Tile[]>();
    for (const tile of play.tiles) byKind.set(tile.kindId, [...(byKind.get(tile.kindId) ?? []), tile]);
    const groups = play.tiles.length === 4 && byKind.size === 2 && [...byKind.values()].every(pair => pair.length === 2)
      ? [...byKind.values()] : [play.tiles];
    return <div className={`daaluu-play-tiles${groups.length === 2 ? ' is-double-pair' : ''}`}>
      {groups.map((group, index) => <span className="daaluu-play-tile-group" key={index}>
        {group.map(tile => <span key={tile.id} data-daaluu-board-tile={tile.id} className={arrivingTileIds.includes(tile.id) ? 'daaluu-tile-arriving' : undefined}>
          <DaaluuTile tile={tile} dead={play.deadTileIds?.includes(tile.id)} deadLabel={mn ? 'Үхсэн' : 'Dead'} />
        </span>)}
      </span>)}
    </div>;
  };
  return <div className="daaluu-current-pile" role="group" aria-label={mn ? 'Одоогийн өрөг' : 'Current played stack'}>
    <div className="daaluu-current-layers">{plays.map((play, index) => <div className="daaluu-play-layer" key={play.seat}
      style={{ '--layer': index, zIndex: play.seat === topSeat ? plays.length : index } as CSSProperties} aria-label={players.find(player => player.seat === play.seat)?.name}>
      {renderTiles(play)}
    </div>)}</div>
    {winner && <div className="trick-label trick-player-name is-winner" title={winner.name}
      aria-label={`${winner.name} · ${mn ? 'Ахалж байна' : 'On top'}`}>👑 {winner.name}</div>}
  </div>;
}

const positions = { 4: ['south', 'west', 'north', 'east'], 5: ['south', 'west', 'north-west', 'north-east', 'east'] };

export function DaaluuTable({ game, mn, children, reactionFor, movingTileIds = [], showJanliiMarker = true, onOfferHouse }: {
  game: GameView; mn: boolean; children: ReactNode; reactionFor?: (playerId: string) => ReactNode; movingTileIds?: string[]; showJanliiMarker?: boolean;
  onOfferHouse?: (buyerSeat: number, pileId: string) => void;
}) {
  const [pendingFriendIds, setPendingFriendIds] = useState<Set<string>>(new Set());
  let state: any = null;
  let actions: any = null;
  try {
    const gs = useGameState();
    state = gs.state;
    actions = gs.actions;
  } catch {
    // optional outside GameProvider
  }
  let openPicker: ((target?: any) => void) | null = null;
  try {
    const ti = useTableInteractions();
    openPicker = ti.openPicker;
  } catch {
    // optional outside of TableInteractionsProvider
  }

  const d = game.daaluu!;
  const janliiKind = DAALUU_KINDS.find(kind => kind.id === d.janlii);
  const freedomHouses = d.freedomHouses ?? 2;
  const layout = positions[game.players.length as 4 | 5];
  const houseChoice = d.stage === 'debt-choice' ? d.debtChoice : null;
  const buyerSeats = houseChoice?.remaining.flatMap((count, seat) => count > 0 ? [seat] : []) ?? [];
  const canSell = !!onOfferHouse && (houseChoice?.surplus[game.mySeat] ?? 0) > 0;
  return <section className={`daaluu-table-scene${d.stage === 'mixing' || d.stage === 'retry' ? ' is-shuffling' : ''}`} aria-label={mn ? 'Даалууны ширээ' : 'Daaluu table'}>
    <div className="daaluu-table-felt" aria-hidden="true" />
    <DaaluuHouseGuides transfers={d.houseTransfers} />
    {showJanliiMarker && janliiKind && <div className="daaluu-janlii-marker" role="group" aria-label={`${mn ? 'Жанлий' : 'Janlii'}: ${janliiKind.name}`}>
      <DaaluuTile tile={{ id: `janlii-marker-${d.shuffle.roundId}`, kindId: janliiKind.id }} />
    </div>}
    {game.players.map(player => {
      const position = layout[(player.seat - game.mySeat + game.players.length) % game.players.length];
      const piles = d.capturePiles[player.seat];
      const pileSlots = new Map(piles.map((pile, index) => [pile.slot ?? index, pile]));
      const lastSlot = Math.max(-1, ...pileSlots.keys());
      const master = d.ledger.masters[player.seat];
      const freedomTarget = d.freedomHousesBySeat?.[player.seat] ?? freedomHouses;
      const isOther = player.playerId !== game.myPlayerId;
      const debts = d.ledger.debt[player.seat].flatMap((amount, creditor) => amount > 0
        ? [{ amount, seat: creditor, name: game.players.find(p => p.seat === creditor)?.name ?? `Player ${creditor + 1}` }]
        : []);
      const debtTotal = debts.reduce((total, debt) => total + debt.amount, 0);
      return <section key={player.playerId} className={`daaluu-seat daaluu-seat-${position}${player.playerId === game.activePlayerId ? ' active' : ''}`}
        data-daaluu-collection-seat={player.seat}
        data-seat={player.seat}
        aria-label={`${player.name} · ${mn ? 'Суудал' : 'Seat'}`}>
        {isOther && <div className="daaluu-seat-badge">
          {player.seat === d.callerSeat && <span className="daaluu-caller-mark" role="img"
            aria-label={mn ? 'Энэ тойргийн дуудагч' : "This round's caller"} title={mn ? 'Энэ тойргийн дуудагч' : "This round's caller"}>
            <Icon name="crown" size={12} />
          </span>}
          {player.playerId === game.activePlayerId && <div className="opponent-turn-indicator" aria-label={`${player.name} · ${mn ? 'Ээлж' : 'Turn'}`}>
            <span className="opponent-turn-pulsar" />
            <span>{mn ? 'Ээлж' : 'Turn'}</span>
          </div>}
          <strong title={player.name}>{player.name}{player.tier === 'paid' ? ' · VIP' : ''}</strong>
          {player.abandoned ? (
            <span className="opponent-status-pill is-abandoned" style={{ margin: '2px 0' }}>🛡️ {mn ? 'Гарсан' : 'Abandoned'}</span>
          ) : player.botControlled || (player.isBot && !player.name.startsWith('Bot ') && !player.name.startsWith('Бот ')) ? (
            <span className="opponent-status-pill is-bot" style={{ margin: '2px 0' }}>🤖 {mn ? 'Бот орлосон' : 'Bot Takeover'}</span>
          ) : !player.connected ? (
            <span className="opponent-status-pill is-offline" style={{ margin: '2px 0' }}>🔌 {mn ? 'Салсан' : 'Offline'}</span>
          ) : player.autoPlay ? (
            <span className="opponent-status-pill is-bot" style={{ margin: '2px 0' }}>🤖 {mn ? 'Автомат' : 'Auto-play'}</span>
          ) : null}
          {!player.isBot && !player.botControlled && player.userId && isOther && state?.profile?.id && (
            <div className="opponent-friend-slot" style={{ margin: '2px 0' }}>
              {state?.friendsData?.friends.some((f: any) => f.userId === player.userId) ? (
                <span className="opponent-friend-tag is-friend" title={mn ? 'Таны найз' : 'Friend'}>
                  ✓ {mn ? 'Найз' : 'Friend'}
                </span>
              ) : state?.friendsData?.outgoingRequests.some((r: any) => r.toUserId === player.userId) || pendingFriendIds.has(player.userId) ? (
                <span className="opponent-friend-tag is-pending" title={mn ? 'Хүсэлт илгээсэн' : 'Request pending'}>
                  ⌛ {mn ? 'Хүлээгдэж буй' : 'Pending'}
                </span>
              ) : (
                <button
                  type="button"
                  className="opponent-add-friend-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (player.userId && actions?.sendFriendRequest) {
                      setPendingFriendIds((prev) => new Set(prev).add(player.userId!));
                      actions.sendFriendRequest({ toUserId: player.userId });
                    }
                  }}
                  title={mn ? `${player.name}-г найзаар нэмэх` : `Add ${player.name} as friend`}
                >
                  + {mn ? 'Найз' : 'Add'}
                </button>
              )}
            </div>
          )}
          <span>{d.ledger.tea[player.seat]} {mn ? 'цай' : 'tea'}</span>
          {(d.stage === 'mixing' || d.stage === 'retry') && player.seat !== d.callerSeat && <small>{d.shuffle.participatedSeats.includes(player.seat) ? mn ? 'Холисон' : 'Mixed' : mn ? 'Холихыг хүлээж байна' : 'Waiting to mix'}</small>}
          {master !== null && <><small>{mn ? 'Ноён' : 'Master'}: {game.players.find(p => p.seat === master)?.name}</small>
            {(d.earnedHouses?.[player.seat] ?? 0) >= freedomTarget && <small>{mn ? 'Тойргийн эцэст чөлөөлөгдөнө' : 'Free at settlement'}</small>}
          </>}
          {debts.length > 0 && <span className="daaluu-debt">
            <button type="button" className="daaluu-debt-trigger" aria-describedby={`daaluu-debt-${player.seat}`}>
              {mn ? 'Өр' : 'Debt'}: {debtTotal}
            </button>
            <span className="daaluu-debt-tooltip" id={`daaluu-debt-${player.seat}`} role="tooltip">
              <strong>{mn ? 'Хэнд төлөх вэ' : 'Owed to'}</strong>
              {debts.map(({ seat, name, amount }) => <span className="daaluu-debt-row" key={seat}>
                <span>{name}</span><span>{amount} {mn ? 'цай' : 'tea'}</span>
              </span>)}
            </span>
          </span>}
          {reactionFor?.(player.playerId)}

          {/* Action button on other player avatar (never on local player) */}
          {isOther && openPicker && (
            <button
              type="button"
              className="seat-throw-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                openPicker({ playerId: player.playerId, name: player.name, seat: player.seat, tier: player.tier });
              }}
              title={mn ? `${player.name} рүү шидэх / тавлах` : `Throw item or taunt ${player.name}`}
              aria-label={mn ? `${player.name} рүү шидэх / тавлах` : `Throw item or taunt ${player.name}`}
            >
              <Icon name="crosshair" size={13} className="seat-throw-icon" />
            </button>
          )}
        </div>}
        <div className={`daaluu-seat-captures${buyerSeats.includes(player.seat) ? ' is-house-buyer' : ''}`}
          id={`daaluu-house-drop-${player.seat}`} data-daaluu-house-buyer={buyerSeats.includes(player.seat) ? '' : undefined}
          aria-label={`${player.name} · ${mn ? 'Гэрүүд' : 'Houses'}`}>
          {piles.length > 0 && <div className="daaluu-capture-tray" aria-label={mn ? 'Хураасан гэрүүд' : 'Captured stacks'}>{Array.from({ length: lastSlot + 1 }, (_, slot) => {
            const pile = pileSlots.get(slot);
            if (!pile) return <span className="daaluu-capture-slot" key={`empty-${slot}`} aria-hidden="true" />;
            const house = <TilePile tiles={pile.tiles} deadTileIds={pile.deadTileIds} movingTileIds={movingTileIds} mn={mn} label={mn ? 'Хураасан өрөг' : 'Captured stack'} />;
            return <span className="daaluu-capture-slot" key={pile.tiles[0].id} data-daaluu-capture-slot={slot}>
              {canSell && player.seat === game.mySeat
              ? <DaaluuHouseDrag sellerSeat={player.seat} buyerSeats={buyerSeats}
                  pileId={pile.tiles[0].id} buyerNames={buyerSeats.map(seat => game.players[seat].name)} mn={mn}
                  onOffer={(buyerSeat, pileId) => onOfferHouse?.(buyerSeat, pileId)}>{house}</DaaluuHouseDrag>
              : house}</span>;
          })}</div>}
          {buyerSeats.includes(player.seat) && piles.length === 0 && <span className="daaluu-house-drop-hint">{mn ? 'Гэрийг энд чирнэ үү' : 'Drag a house here'}</span>}
        </div>
      </section>;
    })}
    <div className="daaluu-table-center">{children}</div>
  </section>;
}

export function DaaluuJanliiChoices({ disabled, onChoose, mn, choicesRef }: { disabled: boolean; onChoose: (kindId: string) => void; mn: boolean; choicesRef?: Ref<HTMLDivElement> }) {
  return <div ref={choicesRef} className="daaluu-call-options" aria-label={mn ? 'Жанлий сонгох' : 'Choose Janlii'}>
    {JANLII_CHOICES.map(kind => <DaaluuTile key={kind.id} tile={{ id: `choice-${kind.id}`, kindId: kind.id }} disabled={disabled} onSelect={() => onChoose(kind.id)} />)}
  </div>;
}
const JANLII_CHOICES = DAALUU_KINDS.filter(kind => kind.copies === 2);
